import { CheckCircleIcon, InformationCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useHouseholds } from "@/api/hooks/useHouseholds.ts";
import { useIsBuildingOwner } from "@/api/hooks/useIsBuildingOwner";
import { UserInfo } from "@/api/rest/models/account";
import { Building } from "@/api/rest/models/building";
import { HouseholdWithOwnership } from "@/api/rest/models/household";
import { Question } from "@/api/rest/models/question";
import { CaseStatus } from "@/api/rest/models/warranty";

export default function ListQuestions({
  questions,
  showHouseholds = false,
  user,
  building,
}: {
  questions: Question[];
  showHouseholds?: boolean;
  user: UserInfo | null;
  building: Building | undefined;
}) {
  const navigate = useNavigate();
  const households = useHouseholds();
  const householdsbyId = useMemo(() => {
    return households.reduce(
      (acc, h) => {
        acc[h.id] = h;
        return acc;
      },
      {} as Record<number, HouseholdWithOwnership>,
    );
  }, [households]);
  const { t } = useTranslation();

  const amIBuildingOwner = useIsBuildingOwner(user, building);

  const sortedQuestions = useMemo(() => {
    const statusOrder = {
      [CaseStatus.BACKLOG]: 1,
      [CaseStatus.NOT_DONE]: 2,
      [CaseStatus.TO_DO]: 3,
      [CaseStatus.IN_PROGRESS]: 4,
      [CaseStatus.REVIEW]: 5,
      [CaseStatus.WAITING]: 6,
      [CaseStatus.SOLVED]: 7,
      [CaseStatus.CLOSED]: 8,
    };

    return questions.slice().sort((a, b) => {
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) {
        return statusDiff;
      }
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });
  }, [questions]);

  return (
    <>
      <h3 className="text-xl font-semibold">{t("question.submittedQuestions")}</h3>
      {sortedQuestions?.map((q) => {
        const shouldHighlight = amIBuildingOwner && Boolean(q.needsBuilderReply);

        return (
          <div
            key={q.id}
            className={`flex flex-col cursor-pointer rounded-md py-2 px-4 ${
              shouldHighlight ? "bg-orange-200 hover:bg-orange-300" : "bg-white hover:bg-orange-100"
            }`}
            onClick={() => navigate(`/question/${q.id}`)}
          >
            {showHouseholds && (
              <span>
                <span className="font-bold">{householdsbyId[q.householdId]?.title || ""}</span> {q.title}
              </span>
            )}
            {!showHouseholds && <span className="font-bold">{q.title}</span>}
            <div className="flex flex-row text-gray-500 text-sm items-center">
              {q.status === CaseStatus.SOLVED && <CheckCircleIcon className="h-4 w-4 mr-1 inline text-green-500" />}
              {shouldHighlight && <InformationCircleIcon className="h-4 w-4 mr-1 inline text-dobu-orange" />}
              {q.status === CaseStatus.CLOSED && <MinusCircleIcon className="h-4 w-4 mr-1 inline text-red-600" />}
              <span>
                {t(q.localisedStatus)}
                {shouldHighlight && ` · ${t("case.awaitingReply")}`}
              </span>
              <span className="flex-grow" />
              <span>{new Date(q.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        );
      })}
    </>
  );
}
